import { useCallback, useEffect, useState } from 'react';
import type { PilotRating, PilotRatingStatus } from '../../types/pilot';
import { usePilotRatings, type PilotTarget } from '../../hooks/usePilotRatings';
import { formatError } from '../../lib/formatError';

const RATING_TYPES = ['class', 'type', 'instrument', 'instructor', 'examiner', 'other'];
const STATUSES: PilotRatingStatus[] = ['valid', 'expired', 'suspended', 'unknown'];

interface Props {
  target: PilotTarget;
}

type DraftRating = Omit<PilotRating, 'id' | 'created_at' | 'updated_at' | 'user_id' | 'org_pilot_id'>;

const EMPTY_DRAFT: DraftRating = {
  rating_type: 'class',
  rating_code: '',
  aircraft_type_or_class: '',
  issue_date: null,
  expiry_date: null,
  revalidation_date: null,
  status: 'unknown',
  remarks: '',
};

export default function PilotRatingsTable({ target }: Props) {
  const api = usePilotRatings();
  const [rows, setRows] = useState<PilotRating[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftRating>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await api.list(target));
      setError(null);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [target, api]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function addRating() {
    if (!draft.rating_code.trim()) {
      setError('Rating code is required');
      return;
    }
    setSaving(true);
    try {
      await api.create({
        target,
        rating_type: draft.rating_type,
        rating_code: draft.rating_code.trim(),
        aircraft_type_or_class: draft.aircraft_type_or_class || null,
        issue_date: draft.issue_date || null,
        expiry_date: draft.expiry_date || null,
        revalidation_date: draft.revalidation_date || null,
        status: draft.status,
        remarks: draft.remarks || null,
      });
      setDraft(EMPTY_DRAFT);
      setError(null);
      await refresh();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  async function removeRow(id: string) {
    if (!confirm('Delete this rating?')) return;
    try {
      await api.remove(id);
      await refresh();
    } catch (err) {
      setError(formatError(err));
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <Th>Type</Th>
              <Th>Code</Th>
              <Th>Aircraft</Th>
              <Th>Issued</Th>
              <Th>Expires</Th>
              <Th>Revalidated</Th>
              <Th>Status</Th>
              <Th>Remarks</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-4 text-center text-gray-500">
                  No ratings yet.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-gray-100">
                <Td>{r.rating_type}</Td>
                <Td>{r.rating_code}</Td>
                <Td>{r.aircraft_type_or_class ?? '—'}</Td>
                <Td>{r.issue_date ?? '—'}</Td>
                <Td>{r.expiry_date ?? '—'}</Td>
                <Td>{r.revalidation_date ?? '—'}</Td>
                <Td>
                  <StatusBadge status={r.status} />
                </Td>
                <Td className="max-w-[220px] truncate" title={r.remarks ?? ''}>
                  {r.remarks ?? '—'}
                </Td>
                <Td>
                  <button
                    type="button"
                    onClick={() => removeRow(r.id)}
                    className="text-xs text-red-600 hover:underline"
                  >
                    Delete
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded border border-gray-200 bg-gray-50 p-3">
        <div className="mb-2 text-sm font-medium text-gray-700">Add rating</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-2 gap-y-3">
          <FormField label="Type">
            <select
              value={draft.rating_type}
              onChange={(e) => setDraft({ ...draft, rating_type: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            >
              {RATING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Code">
            <input
              type="text"
              placeholder="e.g. SEP"
              value={draft.rating_code}
              onChange={(e) => setDraft({ ...draft, rating_code: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
          <FormField label="Aircraft type / class">
            <input
              type="text"
              placeholder="e.g. A320 family, DA40"
              value={draft.aircraft_type_or_class ?? ''}
              onChange={(e) => setDraft({ ...draft, aircraft_type_or_class: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
          <FormField label="Status">
            <select
              value={draft.status}
              onChange={(e) => setDraft({ ...draft, status: e.target.value as PilotRatingStatus })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Issued">
            <input
              type="date"
              value={draft.issue_date ?? ''}
              onChange={(e) => setDraft({ ...draft, issue_date: e.target.value || null })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
          <FormField label="Expires">
            <input
              type="date"
              value={draft.expiry_date ?? ''}
              onChange={(e) => setDraft({ ...draft, expiry_date: e.target.value || null })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
          <FormField label="Revalidated">
            <input
              type="date"
              value={draft.revalidation_date ?? ''}
              onChange={(e) => setDraft({ ...draft, revalidation_date: e.target.value || null })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
          <FormField label="Remarks">
            <input
              type="text"
              placeholder="Optional"
              value={draft.remarks ?? ''}
              onChange={(e) => setDraft({ ...draft, remarks: e.target.value })}
              className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
            />
          </FormField>
        </div>
        <div className="mt-2">
          <button
            type="button"
            disabled={saving}
            onClick={addRating}
            className="rounded bg-[#0B2C45] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#0B2C45]/90 disabled:opacity-50"
          >
            {saving ? 'Adding…' : 'Add rating'}
          </button>
        </div>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
    </div>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

function Th({ children }: { children?: React.ReactNode }) {
  return <th className="px-3 py-2 text-left font-medium">{children}</th>;
}

function Td({
  children,
  className = '',
  title,
}: {
  children: React.ReactNode;
  className?: string;
  title?: string;
}) {
  return (
    <td title={title} className={`px-3 py-2 align-middle ${className}`}>
      {children}
    </td>
  );
}

function StatusBadge({ status }: { status: PilotRatingStatus }) {
  const cls =
    status === 'valid'
      ? 'bg-green-100 text-green-800'
      : status === 'expired'
        ? 'bg-red-100 text-red-800'
        : status === 'suspended'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-gray-100 text-gray-700';
  return (
    <span className={`rounded px-2 py-0.5 text-[10px] uppercase font-medium ${cls}`}>
      {status}
    </span>
  );
}
